import * as THREE from 'three'
import type { DebugMaterialRegistry } from '../debug/debug-materials'
import type { CharacterSchemaType } from './character-schema'
import { SpriteHSM } from './sprite-hsm'
import { computeRelativeYaw, sampleAngleIndex, OCTANT_NAMES } from './angle-resampler'

export type BillboardMode = 'cylindrical' | 'spherical'
export type AnimationState =
  | 'idle'
  | 'walk'
  | 'run'
  | 'crouch_idle'
  | 'crouch_walk'
  | 'crouch_run'
  | 'swim'
  | 'swim_idle'
  | 'attack'
  | 'crouch_attack'
  | 'swim_attack'
  | 'hurt'
  | 'crouch_hurt'
  | 'swim_hurt'
  | 'death'

export interface AnimationDefinition {
  startRow: number
  frameCount: number
  fps: number
}

export interface BillboardSpriteConfig {
  sheetTexturePath: string
  previewTexturePath?: string
  widthMeters: number
  heightMeters: number
  billboardMode?: BillboardMode
  cols?: number // default 8
  rows?: number // default 5 or total sheet rows
  fps?: number // default 6
  animations?: Record<string, AnimationDefinition>
  characterSchema?: CharacterSchemaType
  initialState?: AnimationState
  debugRegistry?: DebugMaterialRegistry
}

const DEFAULT_ANIMATIONS: Record<string, AnimationDefinition> = {
  idle: { startRow: 0, frameCount: 1, fps: 4 },
  walk: { startRow: 1, frameCount: 8, fps: 8 },
  run: { startRow: 1, frameCount: 8, fps: 10 },
  attack: { startRow: 2, frameCount: 1, fps: 8 },
  hurt: { startRow: 3, frameCount: 1, fps: 6 },
  death: { startRow: 4, frameCount: 1, fps: 6 },
}

/**
 * Data-Driven 1998 View-Aligned Billboard Sprite with Continuous Angle Sampling
 * and Hierarchical State Machine (HSM) Animation Management.
 */
export class BillboardSprite {
  readonly mesh: THREE.Mesh
  readonly material: THREE.MeshStandardMaterial
  private readonly texture: THREE.Texture
  private readonly cols: number
  private readonly rows: number
  private readonly billboardMode: BillboardMode
  private readonly animations: Record<string, AnimationDefinition>
  private readonly hsm?: SpriteHSM

  private currentState: AnimationState = 'walk'
  private currentAngleIndex: number = 0
  private currentFrameIndex: number = 0
  private facingYaw: number = 0
  private animTimer: number = 0

  constructor(config: BillboardSpriteConfig) {
    this.cols = config.cols ?? 8
    this.rows = config.rows ?? 5
    this.billboardMode = config.billboardMode ?? 'cylindrical'
    this.animations = config.animations ?? DEFAULT_ANIMATIONS
    this.currentState = config.initialState ?? 'walk'

    if (config.characterSchema) {
      const initialStance = this.currentState.startsWith('crouch_')
        ? 'crouch'
        : this.currentState.startsWith('swim_') || this.currentState === 'swim'
          ? 'swim'
          : 'stand'
      const initialAction = (this.currentState.startsWith('crouch_') || this.currentState.startsWith('swim_')
        ? this.currentState.replace('crouch_', '').replace('swim_', '')
        : this.currentState === 'swim'
          ? 'walk'
          : this.currentState) as any
      this.hsm = new SpriteHSM(config.characterSchema, undefined, initialStance, initialAction)
    }

    // Load texture (safely handle headless / Node.js test environments)
    if (typeof document !== 'undefined') {
      const textureLoader = new THREE.TextureLoader()
      this.texture = textureLoader.load(config.sheetTexturePath)
      this.texture.colorSpace = THREE.SRGBColorSpace
      this.texture.minFilter = THREE.NearestFilter
      this.texture.magFilter = THREE.NearestFilter
      this.texture.generateMipmaps = false
      this.texture.wrapS = THREE.ClampToEdgeWrapping
      this.texture.wrapT = THREE.ClampToEdgeWrapping
    } else {
      this.texture = new THREE.Texture()
    }

    // Set initial UV scale for one cell
    this.texture.repeat.set(1.0 / this.cols, 1.0 / this.rows)
    this.updateUvOffset()

    // Plane geometry
    const geometry = new THREE.PlaneGeometry(config.widthMeters, config.heightMeters)
    geometry.translate(0, config.heightMeters / 2, 0) // Anchor at bottom center

    // Material with crisp alpha testing and zero depth sorting artifacts
    this.material = new THREE.MeshStandardMaterial({
      map: this.texture,
      transparent: true,
      alphaTest: 0.45,
      roughness: 0.6,
      metalness: 0.2,
      side: THREE.DoubleSide,
    })

    this.mesh = new THREE.Mesh(geometry, this.material)
    this.mesh.castShadow = true
    this.mesh.receiveShadow = false
  }

  setFacingYaw(yawRad: number): void {
    this.facingYaw = yawRad
  }

  getFacingYaw(): number {
    return this.facingYaw
  }

  getCurrentAngleIndex(): number {
    return this.currentAngleIndex
  }

  getCurrentFrameIndex(): number {
    return this.currentFrameIndex
  }

  getCurrentAngleName(): string {
    return OCTANT_NAMES[this.currentAngleIndex % 8] || 'FRONT (0°)'
  }

  getHSM(): SpriteHSM | undefined {
    return this.hsm
  }

  setState(state: AnimationState): void {
    if (this.currentState === state) return
    this.currentState = state
    this.animTimer = 0
    this.currentFrameIndex = 0

    if (this.hsm) {
      if (state.startsWith('crouch_')) {
        this.hsm.setStance('crouch')
        this.hsm.setAction(state.replace('crouch_', '') as any)
      } else if (state.startsWith('swim_')) {
        this.hsm.setStance('swim')
        this.hsm.setAction(state.replace('swim_', '') as any)
      } else if (state === 'swim') {
        this.hsm.setStance('swim')
        this.hsm.setAction('walk')
      } else {
        this.hsm.setStance('stand')
        this.hsm.setAction(state as any)
      }
    }

    this.updateUvOffset()
  }

  getState(): AnimationState {
    return this.currentState
  }

  getDisplayStateName(): string {
    if (this.hsm) {
      return this.hsm.getHumanReadableState()
    }
    return this.currentState.replace('_', ' ').toUpperCase()
  }

  cycleState(): AnimationState {
    if (this.hsm) {
      this.hsm.cycleNextTrack()
      const vec = this.hsm.getStateVector()
      if (vec.stance === 'stand') {
        this.currentState = vec.action
      } else {
        this.currentState = `${vec.stance}_${vec.action}` as AnimationState
      }
      this.animTimer = 0
      this.currentFrameIndex = 0
      this.updateUvOffset()
      return this.currentState
    }

    const candidateStates: AnimationState[] = [
      'walk',
      'run',
      'crouch_walk',
      'crouch_run',
      'swim',
      'attack',
      'crouch_attack',
      'swim_attack',
      'hurt',
      'crouch_hurt',
      'swim_hurt',
      'death',
      'idle',
      'crouch_idle',
      'swim_idle',
    ]
    const available = candidateStates.filter((s) => this.animations[s] !== undefined)
    const activeList = available.length > 0 ? available : candidateStates
    const nextIdx = (activeList.indexOf(this.currentState) + 1) % activeList.length
    this.setState(activeList[nextIdx])
    return this.currentState
  }

  update(dt: number, cameraPosition: THREE.Vector3): void {
    // 1. Billboarding rotation
    if (this.billboardMode === 'cylindrical') {
      const dx = cameraPosition.x - this.mesh.position.x
      const dz = cameraPosition.z - this.mesh.position.z
      const camAngle = Math.atan2(dx, dz)
      this.mesh.rotation.set(0, camAngle, 0)

      // 2. Pure continuous angle sampling: f(θ, N)
      const relYaw = computeRelativeYaw(cameraPosition, this.mesh.position, this.facingYaw)
      const angleIdx = sampleAngleIndex(relYaw, this.cols)

      if (angleIdx !== this.currentAngleIndex) {
        this.currentAngleIndex = angleIdx
        this.updateUvOffset()
      }
    } else {
      this.mesh.lookAt(cameraPosition)
    }

    // 3. Dynamic Real-Time Animation Playback
    if (dt > 0) {
      if (this.hsm) {
        this.hsm.update(dt)
        const frame = this.hsm.getKeyframeIndex()
        if (frame !== this.currentFrameIndex) {
          this.currentFrameIndex = frame
          this.updateUvOffset()
        }
      } else {
        const anim = this.animations[this.currentState] ?? { startRow: 0, frameCount: 1, fps: 6 }
        this.animTimer += dt
        const newFrame = Math.floor(this.animTimer * anim.fps) % Math.max(1, anim.frameCount)
        if (newFrame !== this.currentFrameIndex) {
          this.currentFrameIndex = newFrame
          this.updateUvOffset()
        }
      }
    }
  }

  private updateUvOffset(): void {
    if (!this.texture) return

    let startRow = 0
    let frameCount = 1

    if (this.hsm) {
      const track = this.hsm.getCurrentTrack()
      if (track) {
        startRow = track.sectionIndex
        frameCount = track.frameCount
      }
    } else {
      const anim = this.animations[this.currentState] ?? { startRow: 0, frameCount: 1, fps: 6 }
      startRow = anim.startRow
      frameCount = anim.frameCount
    }

    const row = Math.min(this.rows - 1, startRow + (this.currentFrameIndex % Math.max(1, frameCount)))
    const col = this.currentAngleIndex % this.cols

    const u = col / this.cols
    const v = (this.rows - 1 - row) / this.rows
    this.texture.offset.set(u, v)
  }

  dispose(): void {
    this.mesh.geometry.dispose()
    this.material.dispose()
    this.texture.dispose()
  }
}
